import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, MessageSquare, Clock, CheckCircle2 } from 'lucide-react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    topic: 'Mutual Funds',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Relative path for local dev & Hostinger production
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        setSubmitted(true);
        setFormData({ name: '', email: '', phone: '', topic: 'Mutual Funds', message: '' });
        setTimeout(() => setSubmitted(false), 5000);
      } else {
        alert(data.message || 'Error submitting form');
      }
    } catch (error) {
      console.error('Contact Form Error:', error);
      alert('Server Error. Make sure backend is running.');
    }
  };

  return (
    <div className="bg-[#FAF8F5] text-[#0B2540] min-h-screen font-sans selection:bg-[#16325B] selection:text-white py-12 px-6">
      <div className="max-w-7xl mx-auto space-y-12">
        
        {/* Page Header */}
        <div className="text-center max-w-2xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 bg-[#EEF4FA] border border-[#C7D7E8] text-[#16325B] text-xs font-semibold px-4 py-1.5 rounded-full">
            <MessageSquare className="w-4 h-4 text-[#16325B]" />
            We're Here to Help
          </div>

          <h1 className="font-serif-title text-4xl sm:text-5xl font-extrabold text-[#0B2540] tracking-tight">
            Let's Start a <span className="text-[#16325B] italic font-normal">Conversation.</span>
          </h1>

          <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
            Have questions about learning paths, mutual funds, PMS, or wealth distribution? Reach out to us and our team will get back to you within 24 hours.
          </p>
        </div>

        {/* Contact Grid: Info Cards (Left) + Form (Right) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          
          {/* Left Column: Contact Cards */}
          <div className="lg:col-span-5 space-y-6">
            
            {/* Direct Contact Options */}
            <div className="bg-white rounded-3xl p-6 border border-gray-200/80 shadow-sm space-y-6">
              <h3 className="font-serif-title text-xl font-bold text-[#0B2540]">
                Contact Information
              </h3>

              <div className="space-y-4 text-sm">
                <a href="mailto:support@welvate.com" className="flex items-start gap-4 p-3 rounded-2xl hover:bg-[#FAF8F5] transition group">
                  <div className="w-10 h-10 rounded-xl bg-[#EEF4FA] text-[#16325B] flex items-center justify-center shrink-0 group-hover:scale-105 transition">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Email Us</h4>
                    <p className="text-xs text-gray-500">support@welvate.com</p>
                  </div>
                </a>

                <a href="tel:+910000000000" className="flex items-start gap-4 p-3 rounded-2xl hover:bg-[#FAF8F5] transition group">
                  <div className="w-10 h-10 rounded-xl bg-[#EEF4FA] text-[#16325B] flex items-center justify-center shrink-0 group-hover:scale-105 transition">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Call Support</h4>
                    <p className="text-xs text-gray-500">+91 [Phone Number]</p>
                  </div>
                </a>

                <div className="flex items-start gap-4 p-3 rounded-2xl">
                  <div className="w-10 h-10 rounded-xl bg-[#EEF4FA] text-[#16325B] flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Office Location</h4>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      Welvate Wealth Management, Delhi, India
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-3 rounded-2xl">
                  <div className="w-10 h-10 rounded-xl bg-[#EEF4FA] text-[#16325B] flex items-center justify-center shrink-0">
                    <Clock className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Working Hours</h4>
                    <p className="text-xs text-gray-500">Monday – Saturday: 10:00 AM – 7:00 PM</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Advisory Note */}
            <div className="bg-[#0B2540] text-white p-6 rounded-3xl space-y-2 shadow-md">
              <h4 className="font-serif-title font-bold text-base text-[#60A5FA]">100% Transparent & Advisory</h4>
              <p className="text-xs text-gray-300 leading-relaxed">
                We believe in ethical guidance. We do not provide quick stock tips or trading signals. Every discussion focuses on long-term value and compounding.
              </p>
            </div>

          </div>

          {/* Right Column: Contact Form */}
          <div className="lg:col-span-7">
            <div className="bg-white rounded-3xl p-8 border border-gray-200/80 shadow-xl space-y-6">
              
              <div>
                <h3 className="font-serif-title text-2xl font-bold text-[#0B2540]">Send us a Message</h3>
                <p className="text-xs text-gray-500 mt-1">Fill out the form below and we will respond promptly.</p>
              </div>

              {submitted ? (
                <div className="bg-[#EEF4FA] border border-[#16325B]/30 p-6 rounded-2xl text-center space-y-2">
                  <CheckCircle2 className="w-10 h-10 text-[#16325B] mx-auto" />
                  <h4 className="font-bold text-lg text-[#0B2540]">Message Sent Successfully!</h4>
                  <p className="text-xs text-gray-600">Thank you for reaching out. Haritika & the Welvate team will contact you shortly.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-gray-700 block">Your Name *</label>
                      <input 
                        type="text" 
                        required
                        placeholder="e.g. Rahul Sharma"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full bg-[#FAF8F5] border border-gray-200 rounded-xl px-4 py-3 text-xs text-gray-900 focus:outline-none focus:border-[#16325B] transition"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-gray-700 block">Email Address *</label>
                      <input 
                        type="email" 
                        required
                        placeholder="rahul@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="w-full bg-[#FAF8F5] border border-gray-200 rounded-xl px-4 py-3 text-xs text-gray-900 focus:outline-none focus:border-[#16325B] transition"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-gray-700 block">Phone Number</label>
                      <input 
                        type="tel" 
                        placeholder="+91 98765 43210"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="w-full bg-[#FAF8F5] border border-gray-200 rounded-xl px-4 py-3 text-xs text-gray-900 focus:outline-none focus:border-[#16325B] transition"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-gray-700 block">Topic of Interest</label>
                      <select 
                        value={formData.topic}
                        onChange={(e) => setFormData({...formData, topic: e.target.value})}
                        className="w-full bg-[#FAF8F5] border border-gray-200 rounded-xl px-4 py-3 text-xs text-gray-900 focus:outline-none focus:border-[#16325B] transition"
                      >
                        <option value="Mutual Funds">Mutual Funds & SIPs</option>
                        <option value="PMS">PMS & AIF</option>
                        <option value="Pre-IPO">Pre-IPO Unlisted</option>
                        <option value="Learning Paths">Learning Paths & Courses</option>
                        <option value="General Query">General Query</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-gray-700 block">How can we help you? *</label>
                    <textarea 
                      rows={4}
                      required
                      placeholder="Type your message or query here..."
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="w-full bg-[#FAF8F5] border border-gray-200 rounded-xl p-4 text-xs text-gray-900 focus:outline-none focus:border-[#16325B] transition resize-none"
                    />
                  </div>

                  <button 
                    type="submit"
                    className="w-full bg-[#16325B] hover:bg-[#0F213D] text-white font-bold py-3.5 rounded-xl transition-all duration-300 shadow-md shadow-[#16325B]/20 flex items-center justify-center gap-2 text-xs"
                  >
                    <span>Send Message</span>
                    <Send className="w-4 h-4" />
                  </button>
                </form>
              )}

            </div>
          </div>

        </div>

      </div>
    </div>
  );
}